import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DOCUMENT_PAPER_KINDS,
  DOCUMENT_PAPER_LABELS,
  MAX_LOGO_BYTES,
  MAX_PAPER_BYTES,
  SUGGESTED_TERMS,
  type Branding,
  type CompanyProfile,
  type DocumentPaper,
  type DocumentPaperKind,
  type DocumentPapers,
  type TenantSettings,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { downscaleFile } from '../../lib/imageResize';
import { Icon, type IconName } from '../../components/Icon';
import { ErrorState, Modal, PageHead } from '../../components/ui';

interface Profile {
  tenant: { id: string; slug: string; name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

type Tab = 'company' | 'branding' | 'preferences';

/*
 * التبويبُ من المسار لا من حالةٍ محلّية.
 *
 * كانت «الهوية والمستندات» في القائمة تفتح تبويبَ «بيانات الشركة» لأنّ
 * الصفحةَ تبدأ دائمًا من الأوّل. وثلاثةُ مساراتٍ لثلاثة تبويبات تجعل كلَّ
 * رابطٍ في القائمة يصل إلى ما يقول.
 */
const TAB_PATHS: Record<Tab, string> = {
  company: '/settings/company',
  branding: '/settings/branding',
  preferences: '/settings/finance-defaults',
};

export function CompanySettings() {
  const location = useLocation();
  const navigate = useNavigate();
  const tab: Tab =
    (Object.keys(TAB_PATHS) as Tab[]).find((k) => TAB_PATHS[k] === location.pathname) ?? 'company';
  const setTab = (next: Tab) => navigate(TAB_PATHS[next], { replace: true });

  const profile = useQuery({
    queryKey: ['settings'],
    queryFn: () => api<Profile>('/settings'),
  });

  if (profile.isError) {
    return (
      <div className="page">
        <ErrorState error={profile.error} onRetry={() => void profile.refetch()} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHead
        title="إعدادات الشركة"
        subtitle="البيانات والهوية التي تظهر على عروض الأسعار والفواتير"
      />

      {/* تبويبٌ جرافيكيٌّ: أيقونةٌ وعنوانٌ وسطرٌ يشرحُ ما وراءَ كلِّ باب. */}
      <div className="set-tabs" role="tablist">
        {(
          [
            {
              key: 'company' as Tab,
              icon: 'building' as const,
              label: 'بيانات الشركة',
              hint: 'الاسم والعنوان والتواصل',
            },
            {
              key: 'branding' as Tab,
              icon: 'layers' as const,
              label: 'الهوية والمستندات',
              hint: 'الشعار والألوان والنصوص',
            },
            {
              key: 'preferences' as Tab,
              icon: 'receipt' as const,
              label: 'الإعدادات المالية',
              hint: 'العملة والضريبة والشروط',
            },
          ]
        ).map((t) => (
          <button
            key={t.key}
            className={`set-tab${tab === t.key ? ' is-on' : ''}`}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            <span className="set-tab-ic">
              <Icon name={t.icon} size={17} />
            </span>
            <span className="set-tab-txt">
              <strong>{t.label}</strong>
              <span>{t.hint}</span>
            </span>
          </button>
        ))}
      </div>

      {profile.isPending ? (
        <div className="card">
          <div className="card-body">
            <div className="skeleton" style={{ height: 220 }} />
          </div>
        </div>
      ) : (
        <>
          {tab === 'company' && <CompanyForm profile={profile.data} />}
          {tab === 'branding' && <BrandingForm profile={profile.data} />}
          {tab === 'preferences' && <PreferencesForm profile={profile.data} />}
        </>
      )}
    </div>
  );
}

/** حالة الحفظ المشتركة بين النماذج. */
function useSave(path: string) {
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: unknown) => api(`/settings/${path}`, { method: 'PATCH', body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
    },
  });

  /* تأكيد الحفظ يختفي بعد ثوانٍ: بقاؤه يوحي بأن التعديل الحالي محفوظ وهو ليس كذلك. */
  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), 3000);
    return () => clearTimeout(timer);
  }, [saved]);

  return { mutation, saved, clearSaved: () => setSaved(false) };
}

function SaveBar({
  mutation,
  saved,
  onSave,
  disabled,
}: {
  mutation: { isPending: boolean; isError: boolean; error: unknown };
  saved: boolean;
  onSave: () => void;
  disabled?: boolean;
}) {
  return (
    <div className="row mt-4" style={{ gap: 'var(--sp-3)' }}>
      <button
        className="btn btn-primary"
        onClick={onSave}
        disabled={mutation.isPending || disabled}
      >
        {mutation.isPending ? 'جارٍ الحفظ' : 'حفظ'}
      </button>
      {saved && (
        <span className="row small" style={{ color: 'var(--success)' }}>
          <Icon name="check" size={15} />
          حُفظ
        </span>
      )}
      {mutation.isError && (
        <span className="small" style={{ color: 'var(--danger)' }}>
          {mutation.error instanceof Error ? mutation.error.message : 'تعذّر الحفظ.'}
        </span>
      )}
    </div>
  );
}

/* ==========================================================================
   بيانات الشركة
   ========================================================================== */

/**
 * حقلٌ بأيقونةٍ تُعرّفه.
 *
 * صفٌّ من حقولٍ متشابهةٍ يُقرأ بعناوينه وحدَها ببطء. فتسبقُ كلَّ حقلٍ أيقونةٌ
 * تقولُ نوعَه — هاتفٌ أو بريدٌ أو موقعٌ — فتُلتقطُ الخانةُ المطلوبةُ بالنظرة.
 */
function GField({
  icon,
  label,
  hint,
  required,
  wide,
  children,
}: {
  icon: IconName;
  label: string;
  hint?: string;
  required?: boolean;
  wide?: boolean;
  children: ReactNode;
}) {
  return (
    <label className={`gfield${wide ? ' is-wide' : ''}`}>
      <span className="gfield-ic">
        <Icon name={icon} size={16} />
      </span>
      <span className="gfield-body">
        <span className={`gfield-label${required ? ' req' : ''}`}>{label}</span>
        {children}
        {hint && <span className="gfield-hint">{hint}</span>}
      </span>
    </label>
  );
}

type Branch = CompanyProfile['branches'][number];

/**
 * رابطُ الخرائطِ من إحداثيّاتٍ أو من رابطٍ كُتب كما هو.
 *
 * من يلصق رابطَ خريطةٍ جاهزًا يُفتح كما هو، ومن يكتب إحداثيّتين يُبنى له
 * بحثٌ عليهما — فلا يُطالَب بصيغةٍ واحدةٍ يحفظها.
 */
function mapsUrl(v: string): string {
  const s = v.trim();
  if (!s) return '#';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s)}`;
}

function CompanyForm({ profile }: { profile: Profile }) {
  const { mutation, saved, clearSaved } = useSave('company');
  const [form, setForm] = useState({ name: profile.tenant.name, ...profile.company });

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    clearSaved();
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  const setBranches = (branches: Branch[]) => {
    clearSaved();
    setForm((f) => ({ ...f, branches }));
  };
  const patchBranch = (i: number, patch: Partial<Branch>) =>
    setBranches(form.branches.map((b, j) => (j === i ? { ...b, ...patch } : b)));

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="building" size={16} /> بيانات الشركة
        </h2>
        <span className="tiny mute">تظهر على المستندات والمراسلات</span>
      </div>
      <div className="card-body">
        <div className="gfields">
          <GField
            icon="building"
            label="الاسم التجاري"
            required
            hint="يظهر في النظام وفي رسائل البريد."
          >
            <input className="input" value={form.name} onChange={set('name')} />
          </GField>

          <GField icon="briefcase" label="الاسم القانوني" hint="يظهر على المستندات الرسمية.">
            <input
              className="input"
              value={form.legalName}
              onChange={set('legalName')}
              placeholder="شركة … ش.م.م"
            />
          </GField>

          <GField icon="receipt" label="الرقم الضريبي">
            <input
              className="input num"
              value={form.taxNumber}
              onChange={set('taxNumber')}
              dir="ltr"
            />
          </GField>

          <GField icon="book" label="السجل التجاري">
            <input
              className="input num"
              value={form.commercialRegister}
              onChange={set('commercialRegister')}
              dir="ltr"
            />
          </GField>

          <GField icon="phone" label="الهاتف">
            <input className="input num" value={form.phone} onChange={set('phone')} dir="ltr" />
          </GField>

          <GField icon="mail" label="البريد الإلكتروني">
            <input className="input num" value={form.email} onChange={set('email')} dir="ltr" />
          </GField>

          <GField icon="globe" label="الموقع الإلكتروني">
            <input className="input num" value={form.website} onChange={set('website')} dir="ltr" />
          </GField>

          <GField icon="mapPin" label="العنوان" wide>
            <textarea
              className="textarea"
              value={form.address}
              onChange={set('address')}
              style={{ minHeight: 60 }}
            />
          </GField>

          <GField
            icon="target"
            label="الموقع على الخريطة (GPS)"
            hint="إحداثيّات «٣٠.٠٤٤، ٣١.٢٣٦» أو رابط خرائط — يصل به العميل إلى الباب."
            wide
          >
            <div className="gps-row">
              <input
                className="input num"
                dir="ltr"
                placeholder="30.0444, 31.2357"
                value={form.gps}
                onChange={set('gps')}
              />
              <a
                className={`btn btn-sm${form.gps.trim() ? '' : ' is-off'}`}
                href={mapsUrl(form.gps)}
                target="_blank"
                rel="noreferrer"
                title="افتح في الخرائط"
                aria-disabled={!form.gps.trim()}
              >
                <Icon name="mapPin" size={14} /> افتح
              </a>
            </div>
          </GField>
        </div>

        {/* الفروع — تُضاف بزرٍّ ولكلٍّ عنوانُه وهاتفُه وموقعُه. */}
        <div className="branch-head mt-4">
          <h3 className="branch-title">
            <Icon name="mapPin" size={15} /> الفروع
            {form.branches.length > 0 && (
              <span className="branch-count num">{form.branches.length}</span>
            )}
          </h3>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() =>
              setBranches([...form.branches, { name: '', address: '', phone: '', gps: '' }])
            }
          >
            <Icon name="plus" size={14} /> إضافة فرع
          </button>
        </div>

        {form.branches.length === 0 ? (
          <p className="tiny mute" style={{ margin: 0 }}>
            لا فروع — أضف فرعًا إن كان للشركة أكثر من مقرّ، ليظهر عنوانه وهاتفه وموقعه.
          </p>
        ) : (
          <div className="branch-list">
            {form.branches.map((b, i) => (
              <div className="branch-card" key={i}>
                <div className="branch-card-head">
                  <span className="branch-no num">{i + 1}</span>
                  <input
                    className="input branch-name"
                    placeholder={`اسم الفرع (مثلًا: فرع المعادي)`}
                    value={b.name}
                    onChange={(e) => patchBranch(i, { name: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-danger"
                    title="حذف الفرع"
                    aria-label="حذف الفرع"
                    onClick={() => setBranches(form.branches.filter((_, j) => j !== i))}
                  >
                    <Icon name="trash" size={14} />
                  </button>
                </div>
                <div className="gfields">
                  <GField icon="mapPin" label="عنوان الفرع" wide>
                    <input
                      className="input"
                      value={b.address}
                      onChange={(e) => patchBranch(i, { address: e.target.value })}
                    />
                  </GField>
                  <GField icon="phone" label="هاتف الفرع">
                    <input
                      className="input num"
                      dir="ltr"
                      value={b.phone}
                      onChange={(e) => patchBranch(i, { phone: e.target.value })}
                    />
                  </GField>
                  <GField icon="target" label="الموقع على الخريطة (GPS)">
                    <div className="gps-row">
                      <input
                        className="input num"
                        dir="ltr"
                        placeholder="30.0444, 31.2357"
                        value={b.gps}
                        onChange={(e) => patchBranch(i, { gps: e.target.value })}
                      />
                      <a
                        className={`btn btn-sm${b.gps.trim() ? '' : ' is-off'}`}
                        href={mapsUrl(b.gps)}
                        target="_blank"
                        rel="noreferrer"
                        title="افتح في الخرائط"
                        aria-disabled={!b.gps.trim()}
                      >
                        <Icon name="mapPin" size={14} />
                      </a>
                    </div>
                  </GField>
                </div>
              </div>
            ))}
          </div>
        )}

        <SaveBar
          mutation={mutation}
          saved={saved}
          onSave={() => mutation.mutate(form)}
          disabled={form.name.trim().length < 2}
        />
      </div>
    </div>
  );
}

/* ==========================================================================
   الهوية
   ========================================================================== */

/**
 * بياناتُ التحويلِ البنكيّ — في تبويبِ الهويّةِ لا بياناتِ الشركة.
 *
 * موضعُها من النظامِ حيثُ تُقرأ: تُطبع أسفلَ الفاتورةِ ويُخفيها مفتاحٌ في هذا
 * التبويبِ نفسِه، فجمعُها مع الشعارِ ونصوصِ المستندِ أقربُ من جمعِها مع السجلِّ
 * التجاريّ. وحفظُها يبقى على مسارِ «الشركة» لأنّها من بياناتها لا من هويّتها.
 */
function BankBlock({ profile }: { profile: Profile }) {
  const { mutation, saved, clearSaved } = useSave('company');
  const [form, setForm] = useState({
    bankName: profile.company.bankName,
    bankAccount: profile.company.bankAccount,
    bankIban: profile.company.bankIban,
  });

  const set = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    clearSaved();
    setForm((f) => ({ ...f, [key]: e.target.value }));
  };

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="bank" size={16} /> بيانات التحويل البنكي
        </h2>
        <span className="tiny mute">تظهر أسفل الفواتير</span>
      </div>
      <div className="card-body">
        <p className="tiny mute" style={{ marginTop: 0 }}>
          تظهر أسفل الفواتير ليعرف العميل أين يحوّل — ويُخفيها مفتاح «إظهار بيانات التحويل
          البنكي» في نصوص المستند.
        </p>

        <div className="gfields mt-3">
          <GField icon="bank" label="اسم البنك والفرع" wide>
            <input className="input" value={form.bankName} onChange={set('bankName')} />
          </GField>

          <GField icon="wallet" label="رقم الحساب">
            <input
              className="input num"
              value={form.bankAccount}
              onChange={set('bankAccount')}
              dir="ltr"
            />
          </GField>

          <GField icon="clipboard" label="الآيبان">
            <input
              className="input num"
              value={form.bankIban}
              onChange={set('bankIban')}
              dir="ltr"
            />
          </GField>
        </div>

        <SaveBar mutation={mutation} saved={saved} onSave={() => mutation.mutate(form)} />
      </div>
    </div>
  );
}

function BrandingForm({ profile }: { profile: Profile }) {
  const { mutation, saved, clearSaved } = useSave('branding');
  const [form, setForm] = useState<Branding>(profile.branding);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /* معاينةٌ كاملةٌ قبل الحفظ: يرى أثرَ الشعارِ واللونِ والنصوصِ على مستندٍ
   * حقيقيٍّ لا على حقولٍ متفرّقة. */
  const [preview, setPreview] = useState(false);

  const set = <K extends keyof Branding>(key: K, value: Branding[K]) => {
    clearSaved();
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <>
      {preview && (
        <BrandPreviewModal form={form} profile={profile} onClose={() => setPreview(false)} />
      )}
      <div className="two-col">

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">
            <Icon name="layers" size={16} /> الشعار والألوان
          </h2>
          <button className="btn btn-sm" onClick={() => setPreview(true)}>
            <Icon name="eye" size={14} /> معاينة
          </button>
        </div>
        <div className="card-body">
          {uploadError && <div className="alert alert-danger mb-4">{uploadError}</div>}

          <ImageField
            label="الشعار"
            hint="يظهر أعلى المستندات. يُفضَّل PNG بخلفية شفافة."
            value={form.logoUrl}
            onChange={(v) => set('logoUrl', v)}
            onError={setUploadError}
          />

          <div className="mt-4">
            <ImageField
              label="الختم أو التوقيع"
              hint="يظهر أسفل المستند. اتركه فارغًا لعرض سطر توقيع."
              value={form.stampUrl}
              onChange={(v) => set('stampUrl', v)}
              onError={setUploadError}
            />
          </div>

          <div className="form-grid mt-4">
            <ColorField
              label="اللون الأساسي"
              value={form.primary}
              onChange={(v) => set('primary', v)}
              hint="الترويسة وعناوين الجداول."
            />
            <ColorField
              label="لون النص"
              value={form.ink}
              onChange={(v) => set('ink', v)}
              hint="نص المستند."
            />
          </div>

          <SaveBar mutation={mutation} saved={saved} onSave={() => mutation.mutate(form)} />
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2 className="card-title">
            <Icon name="note" size={16} /> نصوص المستند
          </h2>
          <button className="btn btn-sm" onClick={() => setPreview(true)}>
            <Icon name="eye" size={14} /> معاينة
          </button>
        </div>
        <div className="card-body">
          <div className="field">
            <label className="label" htmlFor="b-header">
              سطر الترويسة
            </label>
            <input
              id="b-header"
              className="input"
              value={form.documentHeader}
              onChange={(e) => set('documentHeader', e.target.value)}
              placeholder="حلول تسويق وإنتاج محتوى"
            />
            <span className="tiny mute">يظهر تحت اسم الشركة مباشرة.</span>
          </div>

          <div className="field mt-4">
            <label className="label" htmlFor="b-footer">
              نص التذييل
            </label>
            <textarea
              id="b-footer"
              className="textarea"
              value={form.documentFooter}
              onChange={(e) => set('documentFooter', e.target.value)}
              placeholder="شكرًا لثقتكم. لأي استفسار يُرجى التواصل على…"
            />
          </div>

          <div className="col mt-4">
            <label className="row" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.showTaxNumber}
                onChange={(e) => set('showTaxNumber', e.target.checked)}
              />
              <span className="small">إظهار الرقم الضريبي على المستندات</span>
            </label>
            <label className="row" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={form.showBankDetails}
                onChange={(e) => set('showBankDetails', e.target.checked)}
              />
              <span className="small">إظهار بيانات التحويل البنكي على الفواتير</span>
            </label>
          </div>

          {/* معاينة مصغّرة تُظهر أثر اللون فورًا بدل فتح مستند وطباعته. */}
          <div className="brand-preview mt-4" style={{ ['--p' as string]: form.primary }}>
            <div className="brand-preview-head">
              {form.logoUrl ? (
                <img src={form.logoUrl} alt="" className="brand-preview-logo" />
              ) : (
                <div className="brand-preview-placeholder">شعار</div>
              )}
              <div>
                <div className="brand-preview-name" style={{ color: form.primary }}>
                  {profile.company.legalName || profile.tenant.name}
                </div>
                <div className="tiny mute">{form.documentHeader || 'سطر الترويسة'}</div>
              </div>
            </div>
            <div className="brand-preview-bar" />
            <div className="tiny mute mt-2">معاينة ترويسة المستند</div>
          </div>
        </div>
      </div>
      </div>

      {/* أوراقُ المستندات تحت الشعار: الورقةُ تحلّ محلّ الشعار والترويسة على المطبوع. */}
      <PapersCard />

      {/* بياناتُ التحويلِ البنكيِّ — انتقلت إلى هنا لأنّها تُطبع على المستند. */}
      <BankBlock profile={profile} />
    </>
  );
}

/**
 * معاينةُ المستندِ قبل الحفظ.
 *
 * الحقولُ متفرّقةٌ والأثرُ مجتمِع: لونٌ وشعارٌ وترويسةٌ وتذييلٌ تلتقي في ورقةٍ
 * واحدة. فتُعرضُ هنا ورقةٌ وهميّةٌ بالقيمِ الحاليّةِ — بما لم يُحفَظ بعد — حتى
 * لا يُكتشَفَ الخطأُ بعد إرسالِ فاتورةٍ إلى عميل.
 */
function BrandPreviewModal({
  form,
  profile,
  onClose,
}: {
  form: Branding;
  profile: Profile;
  onClose: () => void;
}) {
  const name = profile.company.legalName || profile.tenant.name;
  const cur = profile.tenant.currency === 'EGP' ? 'ج.م' : profile.tenant.currency;

  return (
    <Modal title="معاينة المستند" onClose={onClose} wide>
      <div className="doc-prev" style={{ ['--p' as string]: form.primary, ['--k' as string]: form.ink }}>
        {/* الترويسة */}
        <div className="doc-prev-head">
          <div className="doc-prev-brand">
            {form.logoUrl ? (
              <img src={form.logoUrl} alt="" className="doc-prev-logo" />
            ) : (
              <div className="doc-prev-logo is-empty">شعار</div>
            )}
            <div>
              <div className="doc-prev-name">{name}</div>
              {form.documentHeader && <div className="doc-prev-sub">{form.documentHeader}</div>}
            </div>
          </div>
          <div className="doc-prev-meta">
            <strong>فاتورة</strong>
            <span className="num" dir="ltr">
              INV-2026-0042
            </span>
            {form.showTaxNumber && profile.company.taxNumber && (
              <span className="num" dir="ltr">
                ض.ب {profile.company.taxNumber}
              </span>
            )}
          </div>
        </div>

        <div className="doc-prev-rule" />

        {/* جدولُ بنودٍ وهميّ */}
        <table className="doc-prev-table">
          <thead>
            <tr>
              <th>البند</th>
              <th>الكمية</th>
              <th>السعر</th>
              <th>الإجمالي</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>خدمةُ استشارةٍ تسويقيّة</td>
              <td className="num">2</td>
              <td className="num">1,500</td>
              <td className="num">3,000</td>
            </tr>
            <tr>
              <td>إنتاجُ محتوى — باقةٌ شهريّة</td>
              <td className="num">1</td>
              <td className="num">4,500</td>
              <td className="num">4,500</td>
            </tr>
          </tbody>
        </table>

        <div className="doc-prev-total">
          <span>الإجمالي</span>
          <strong className="num">
            7,500 {cur}
          </strong>
        </div>

        {form.showBankDetails && (
          <div className="doc-prev-bank">
            <strong>بيانات التحويل</strong>
            <span className="tiny">تظهر هنا بيانات الحساب البنكيّ المسجَّلة للشركة.</span>
          </div>
        )}

        {/* التذييل والختم */}
        <div className="doc-prev-foot">
          <p>{form.documentFooter || 'نصُّ التذييل يظهر هنا.'}</p>
          {form.stampUrl ? (
            <img src={form.stampUrl} alt="" className="doc-prev-stamp" />
          ) : (
            <div className="doc-prev-sign">التوقيع</div>
          )}
        </div>
      </div>

      <p className="tiny mute mt-3">
        معاينةٌ بالقيمِ الحاليّةِ قبل الحفظ — الأرقامُ والبنودُ وهميّةٌ للتوضيح.
      </p>
      <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn" onClick={onClose}>
          إغلاق
        </button>
      </div>
    </Modal>
  );
}

function ColorField({
  label,
  value,
  onChange,
  hint,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="row">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="color-input"
          aria-label={label}
        />
        <input
          className="input num"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          dir="ltr"
          style={{ width: 110 }}
        />
      </div>
      {hint && <span className="tiny mute">{hint}</span>}
    </div>
  );
}

function ImageField({
  label,
  hint,
  value,
  onChange,
  onError,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
  onError: (message: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  /*
   * الصورة تُقرأ كسلسلة بيانات مضمّنة.
   *
   * الحد الأقصى يُفحص هنا قبل الرفع: رفض الخادم بعد رفع ملف كبير يهدر وقت
   * المستخدم ويترك سبب الرفض غامضًا.
   */
  function onFile(file: File): void {
    onError(null);
    if (file.size > MAX_LOGO_BYTES) {
      onError(
        `حجم الملف ${Math.round(file.size / 1024)} كيلوبايت، والحد ${Math.round(MAX_LOGO_BYTES / 1024)}. اضغط الصورة أو استخدم مقاسًا أصغر.`,
      );
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result));
    reader.onerror = () => onError('تعذّرت قراءة الملف.');
    reader.readAsDataURL(file);
  }

  return (
    <div className="field">
      <span className="label">{label}</span>
      <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <div className="logo-slot">
          {value ? <img src={value} alt="" /> : <span className="tiny mute">لا يوجد</span>}
        </div>
        <div className="col" style={{ gap: 'var(--sp-2)' }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFile(file);
              e.target.value = '';
            }}
          />
          <button className="btn btn-sm" onClick={() => inputRef.current?.click()} type="button">
            <Icon name="plus" size={14} />
            اختيار ملف
          </button>
          {value && (
            <button className="btn btn-sm" onClick={() => onChange('')} type="button">
              <Icon name="trash" size={14} />
              إزالة
            </button>
          )}
          <span className="tiny mute">{hint}</span>
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
   أوراقُ المستندات — ليتر هيد A4
   ========================================================================== */

/** ارتفاعُ A4 إلى عرضه: 297 ÷ 210 مم. */
const A4_RATIO = 297 / 210;

/**
 * الورقةُ صورةً مضغوطةً تكفي خلفيّةً مطبوعة.
 *
 * ملفُّ 300dpi (2480×3508) يتجاوز بضعة ميجابايت، فيُصغَّر إلى نحو 200dpi
 * (JPEG) قبل الحفظ — لا فرقَ يُرى في خلفيّةٍ مطبوعة. وإن بقي أكبر من الحدّ
 * صُغّر مرّةً أخرى بجودةٍ أقلّ قبل أن يُرفض.
 */
async function paperDataUrl(file: File): Promise<string> {
  let url = await downscaleFile(file, 2339, 0.85);
  if (url.length > MAX_PAPER_BYTES) url = await downscaleFile(file, 1754, 0.75);
  if (url.length > MAX_PAPER_BYTES) throw new Error('الصورة كبيرة حتى بعد ضغطها — استخدم صورةً أبسط أو أصغر.');
  return url;
}

/** نسبةُ ارتفاع الصورة إلى عرضها — لتنبيه المالك إن لم تكن A4 طوليّة. */
function imageRatio(url: string): Promise<number> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalHeight / Math.max(1, img.naturalWidth));
    img.onerror = () => resolve(A4_RATIO);
    img.src = url;
  });
}

/**
 * أوراقُ المستندات: ليتر هيد عامّ، وورقةٌ للفاتورة، وأخرى لكشف الحساب.
 *
 * المستندُ المطبوع يُكتب فوق الورقة داخل حدودٍ يضبطها المالك (تحت ترويسة
 * التصميم وفوق تذييله)، وتختفي ترويسةُ النظام لأنّ الورقة تحملها. وحفظُها
 * على مسارٍ خاصّ (`/settings/papers`) لا مع الهويّة: صورٌ كبيرة لا تُحمَّل
 * مع كلّ قراءةٍ للإعدادات.
 */
function PapersCard() {
  const papers = useQuery({
    queryKey: ['settings', 'papers'],
    queryFn: () => api<DocumentPapers>('/settings/papers'),
  });

  if (papers.isError) return <ErrorState error={papers.error} onRetry={() => void papers.refetch()} />;
  if (!papers.data) return null;
  return <PapersForm initial={papers.data} />;
}

function PapersForm({ initial }: { initial: DocumentPapers }) {
  const { mutation, saved, clearSaved } = useSave('papers');
  const [form, setForm] = useState<DocumentPapers>(initial);
  const [error, setError] = useState<string | null>(null);

  const set = (kind: DocumentPaperKind, patch: Partial<DocumentPaper>) => {
    clearSaved();
    setForm((f) => ({ ...f, [kind]: { ...f[kind], ...patch } }));
  };

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="printer" size={16} /> أوراق المستندات (A4)
        </h2>
        <span className="tiny mute">تُطبع عليها المستندات بدل ترويسة النظام</span>
      </div>
      <div className="card-body">
        <p className="tiny mute" style={{ marginTop: 0 }}>
          ارفع تصميم ورقة الشركة فتُطبع المستندات وملفات PDF عليها: التقارير والكشوف وعروض الأسعار
          والعقود على الليتر هيد العام، والفواتير وكشوف الحساب على ورقتها إن وُجدت. ويختفي من المستند
          الشعار واسم الشركة وبياناتها وبيانات البنك لأن الورقة تحملها — والبيانات نفسها تبقى محفوظة
          هنا. الصور فقط (PNG أو JPG).
        </p>
        {error && <div className="alert alert-danger mb-4">{error}</div>}

        <div className="paper-grid mt-3">
          {DOCUMENT_PAPER_KINDS.map((kind) => (
            <PaperField
              key={kind}
              kind={kind}
              value={form[kind]}
              onChange={(patch) => set(kind, patch)}
              onError={setError}
            />
          ))}
        </div>

        <SaveBar mutation={mutation} saved={saved} onSave={() => mutation.mutate(form)} />
      </div>
    </div>
  );
}

function PaperField({
  kind,
  value,
  onChange,
  onError,
}: {
  kind: DocumentPaperKind;
  value: DocumentPaper;
  onChange: (patch: Partial<DocumentPaper>) => void;
  onError: (message: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [ratioWarn, setRatioWarn] = useState(false);

  async function onFile(file: File): Promise<void> {
    onError(null);
    setBusy(true);
    try {
      const url = await paperDataUrl(file);
      setRatioWarn(Math.abs((await imageRatio(url)) / A4_RATIO - 1) > 0.03);
      onChange({ imageUrl: url });
    } catch (e) {
      onError(e instanceof Error ? e.message : 'تعذّرت قراءة الملف.');
    } finally {
      setBusy(false);
    }
  }

  /* حدُّ الكتابة بالمليمتر — وفي المعاينة المصغّرة نسبةً من عرض A4 وارتفاعه. */
  const mm = (key: 'top' | 'bottom' | 'side', label: string, max: number) => (
    <label className="paper-margin">
      <span className="tiny mute">{label}</span>
      <input
        className="input num"
        type="number"
        min={0}
        max={max}
        step={1}
        value={value[key]}
        onChange={(e) => {
          const n = Number(e.target.value);
          if (Number.isFinite(n)) onChange({ [key]: Math.min(max, Math.max(0, n)) });
        }}
      />
    </label>
  );

  return (
    <div className="paper-field">
      <span className="label">{DOCUMENT_PAPER_LABELS[kind]}</span>
      <div className="row" style={{ alignItems: 'flex-start', gap: 'var(--sp-3)' }}>
        <div
          className={`paper-thumb${value.imageUrl ? '' : ' is-empty'}`}
          style={value.imageUrl ? { backgroundImage: `url("${value.imageUrl}")` } : undefined}
          title="معاينة الورقة ومساحة الكتابة"
        >
          {!value.imageUrl && (
            <span className="tiny mute">{kind === 'letterhead' ? 'لا يوجد' : 'يُستعمل الليتر هيد'}</span>
          )}
          <span
            className="paper-thumb-box"
            style={{
              top: `${(value.top / 297) * 100}%`,
              bottom: `${(value.bottom / 297) * 100}%`,
              insetInline: `${(value.side / 210) * 100}%`,
            }}
          />
        </div>
        <div className="col" style={{ gap: 'var(--sp-2)', minWidth: 0 }}>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onFile(file);
              e.target.value = '';
            }}
          />
          <button className="btn btn-sm" onClick={() => inputRef.current?.click()} type="button" disabled={busy}>
            <Icon name="plus" size={14} />
            {busy ? 'جارٍ التجهيز…' : 'اختيار ملف'}
          </button>
          {value.imageUrl && (
            <button
              className="btn btn-sm"
              onClick={() => {
                setRatioWarn(false);
                onChange({ imageUrl: '' });
              }}
              type="button"
            >
              <Icon name="trash" size={14} />
              إزالة
            </button>
          )}
          <span className="tiny mute">
            صورة A4 بدقة 300dpi (2480×3508) — النظام يكتب البيانات فوقها في المساحة الفارغة.
          </span>
          {ratioWarn && (
            <span className="tiny" style={{ color: 'var(--warning)' }}>
              أبعاد الصورة ليست بنسبة A4 الطولية، فستُمَطّ لتملأ الصفحة.
            </span>
          )}
        </div>
      </div>
      <div className="paper-margins">
        {mm('top', 'الهامش العلوي (مم)', 150)}
        {mm('bottom', 'الهامش السفلي (مم)', 150)}
        {mm('side', 'الهامشان الجانبيان (مم)', 60)}
      </div>
    </div>
  );
}

/* ==========================================================================
   الإعدادات المالية
   ========================================================================== */

function PreferencesForm({ profile }: { profile: Profile }) {
  const { mutation, saved, clearSaved } = useSave('preferences');
  const [form, setForm] = useState<TenantSettings>(profile.settings);

  const set = <K extends keyof TenantSettings>(key: K, value: TenantSettings[K]) => {
    clearSaved();
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="receipt" size={16} /> الإعدادات المالية
        </h2>
        <span className="tiny mute">قيمٌ افتراضيّةٌ تُملأ بها المستندات الجديدة</span>
      </div>
      <div className="card-body">
        <div className="form-grid">
          <div className="field">
            <label className="label" htmlFor="p-tax">
              نسبة الضريبة الافتراضية
            </label>
            <input
              id="p-tax"
              className="input num"
              type="number"
              min="0"
              max="100"
              step="any"
              value={form.taxRate}
              onChange={(e) => set('taxRate', Number(e.target.value))}
            />
            <span className="tiny mute">
              تُطبَّق على المستندات الجديدة وتقبل التعديل في كل مستند.
            </span>
          </div>

          <div className="field">
            <label className="label" htmlFor="p-due">
              مهلة سداد الفاتورة
            </label>
            <input
              id="p-due"
              className="input num"
              type="number"
              min="0"
              max="365"
              value={form.invoiceDueDays}
              onChange={(e) => set('invoiceDueDays', Number(e.target.value))}
            />
            <span className="tiny mute">بالأيام من تاريخ الإصدار.</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="p-stmt">
              يوم إصدار كشوف الحساب
            </label>
            <select
              id="p-stmt"
              className="select"
              value={String(form.statementDay ?? 0)}
              onChange={(e) => set('statementDay', Number(e.target.value))}
            >
              <option value="0">آخر يوم من الشهر</option>
              {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>
                  يوم {d} من الشهر التالي
                </option>
              ))}
            </select>
            <span className="tiny mute">
              يُصدر النظام كشفًا شهريًّا لكل عميل آليًّا في هذا الموعد — ويظهر في ملفه.
            </span>
          </div>

          <div className="field">
            <label className="label" htmlFor="p-valid">
              صلاحية عرض السعر
            </label>
            <input
              id="p-valid"
              className="input num"
              type="number"
              min="1"
              max="365"
              value={form.quotationValidityDays}
              onChange={(e) => set('quotationValidityDays', Number(e.target.value))}
            />
            <span className="tiny mute">بالأيام. بعدها يُعلَّم العرض منتهي الصلاحية.</span>
          </div>

          <div className="field">
            <label className="label" htmlFor="p-currency">
              العملة
            </label>
            <select
              id="p-currency"
              className="select"
              value={form.currency}
              onChange={(e) => {
                const labels: Record<string, string> = {
                  EGP: 'ج.م',
                  SAR: 'ر.س',
                  AED: 'د.إ',
                  USD: '$',
                };
                set('currency', e.target.value);
                set('currencyLabel', labels[e.target.value] ?? e.target.value);
              }}
            >
              <option value="EGP">جنيه مصري</option>
              <option value="SAR">ريال سعودي</option>
              <option value="AED">درهم إماراتي</option>
              <option value="USD">دولار أمريكي</option>
            </select>
            <span className="tiny mute">تُستخدم في العرض والتفقيط على المستندات.</span>
          </div>
        </div>

        <h3 className="label mt-4 mb-4">الشروط الافتراضية</h3>
        <p className="tiny mute mb-4">
          تُدرج تلقائيًا في المستندات الجديدة وتقبل التعديل في كل مستند على حدة.
        </p>

        <div className="form-grid">
          <TermsField
            id="p-qterms"
            label="شروط عروض الأسعار"
            value={form.defaultQuotationTerms}
            onChange={(v) => set('defaultQuotationTerms', v)}
            suggestions={SUGGESTED_TERMS.quotation}
          />
          <TermsField
            id="p-iterms"
            label="شروط الفواتير"
            value={form.defaultInvoiceTerms}
            onChange={(v) => set('defaultInvoiceTerms', v)}
            suggestions={SUGGESTED_TERMS.invoice}
          />
          <TermsField
            id="p-cterms"
            label="شروط العقود"
            value={form.defaultContractTerms}
            onChange={(v) => set('defaultContractTerms', v)}
            suggestions={SUGGESTED_TERMS.contract}
          />
        </div>

        <SaveBar mutation={mutation} saved={saved} onSave={() => mutation.mutate(form)} />
      </div>
    </div>
  );
}

function TermsField({
  id,
  label,
  value,
  onChange,
  suggestions,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  suggestions: readonly string[];
}) {
  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <textarea
        id={id}
        className="textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ minHeight: 120 }}
      />
      {/*
        الشروط تُقترح ولا تُفرض: الشرط التزام قانوني، وإدراجه تلقائيًا
        يجعل الشركة تُصدر مستندات بنصوص لم تقرأها.
      */}
      <button
        type="button"
        className="btn btn-sm mt-2"
        onClick={() => onChange(suggestions.join('\n'))}
      >
        إدراج صياغة مقترحة
      </button>
      <span className="tiny mute">راجعها قبل الاعتماد — الشروط التزام على شركتك.</span>
    </div>
  );
}
